import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import type { FloraPage } from '../../../api/flora-pages-client/flora-pages-client.type';
import { FloraPageVisibility } from '../../../api/flora-pages-client/flora-pages-client.type';
import { Icon } from '../../../ui-library/icon/icon';

interface FloraPageVisibilityMenuProps {
  page: FloraPage;
  onToggleVisibility: (page: FloraPage) => void;
}

/**
 * Owner-only visibility control for the full-window page viewer's floating
 * toolbar. Shows the page's current Private/Organization state and lets the
 * owner switch it — reuses the same toggle mutation as the Pages gallery's
 * kebab menu (there are only two states, so selecting the non-current option
 * is equivalent to toggling) so both surfaces stay consistent.
 *
 * @param {FloraPageVisibilityMenuProps} props - The page and its visibility toggle handler.
 * @returns {JSX.Element} The visibility menu trigger and dropdown.
 */
export function FloraPageVisibilityMenu({ page, onToggleVisibility }: FloraPageVisibilityMenuProps) {
  const isShared = page.visibility === FloraPageVisibility.Organization;

  const handleSelect = (visibility: FloraPageVisibility) => {
    if (visibility === page.visibility) return;
    onToggleVisibility(page);
  };

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <TriggerButton type="button" aria-label="Page visibility" title="Page visibility">
          <Icon name={isShared ? 'group' : 'lock'} size={16} />
          <span>{isShared ? 'Shared' : 'Private'}</span>
          <Icon name="expand_more" size={16} />
        </TriggerButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<Icon name="lock" size={16} />}
          rightSection={!isShared ? <Icon name="check" size={16} /> : undefined}
          onClick={() => handleSelect(FloraPageVisibility.Private)}
        >
          Private to me
        </Menu.Item>
        <Menu.Item
          leftSection={<Icon name="group" size={16} />}
          rightSection={isShared ? <Icon name="check" size={16} /> : undefined}
          onClick={() => handleSelect(FloraPageVisibility.Organization)}
        >
          Shared with organization
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const TriggerButton = styled.button`
  background: none;
  border: none;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-base);

  &:hover {
    background: var(--sky-lightest);
  }
`;
